/**
 * usePopularCategories Hook
 *
 * React Query hook for fetching popular/featured categories with caching and auto-refetch
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { fetchPopularCategories } from '../../api/fetchPopularCategories';
import type { Category } from '../../types';

export interface UsePopularCategoriesParams {
  limit?: number;
  useMock?: boolean;
  enabled?: boolean;
}

export interface UsePopularCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  isRefetching: boolean;
}

/**
 * Query key factory for popular categories
 */
export const popularCategoriesKeys = {
  all: ['categories', 'popular'] as const,
  byLimit: (limit: number, useMock: boolean) =>
    [...popularCategoriesKeys.all, limit, useMock] as const,
};

/**
 * Hook for fetching popular categories with React Query
 *
 * Features:
 * - Automatic caching (5 minutes stale time)
 * - Auto-refetch every 5 minutes
 * - Optimistic updates support
 * - Background refetching
 * - Auto-detects development mode from NEXT_PUBLIC_IS_DEVELOPMENT env var
 *
 * @param params - Configuration for fetching categories
 * @returns Object with categories, loading state, error, and refetch function
 */
export function usePopularCategories(
  params: UsePopularCategoriesParams = {},
): UsePopularCategoriesReturn {
  const { limit = 4, useMock = false, enabled = true } = params;

  const {
    data: categories = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: popularCategoriesKeys.byLimit(limit, useMock),
    queryFn: () => fetchPopularCategories({ limit, useMock }),
    enabled,
    // Override global defaults if needed (already configured in QueryProvider)
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    categories,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refetch: () => {
      refetch();
    },
    isRefetching,
  };
}
